import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Icon, PasswordInput, Result, Space, Text, Title } from "@app/ui";
import { api } from "../api/client.js";
import { AuthShell } from "../auth/AuthShell.js";
import { PasswordChecklist, passwordMeetsPolicy } from "../auth/PasswordChecklist.js";
import { useT } from "../i18n.js";
import { ErrorAlert, useFormErrors } from "../errors/index.js";

/**
 * Password-reset landing page. Takes the token from the emailed link, collects a
 * new password (validated against the same policy the API enforces), and submits
 * it. On success all sessions were revoked server-side, so the user signs in
 * fresh. Public route (the token is the credential).
 */
export function ResetPasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [pwdRejected, setPwdRejected] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const errs = useFormErrors();

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!passwordMeetsPolicy(newPassword)) {
      setPwdRejected(true);
      return;
    }
    errs.reset();
    setBusy(true);
    void (async () => {
      try {
        await api<void>("/auth/password/reset", {
          method: "POST",
          body: { token, newPassword },
          noRetry: true,
        });
        setDone(true);
      } catch (err) {
        errs.handle(err);
      } finally {
        setBusy(false);
      }
    })();
  };

  // No token in the link — nothing to reset.
  if (!token) {
    return (
      <AuthShell>
        <Result
          status="error"
          title={t("reset.errorTitle")}
          subTitle={t("reset.missingToken")}
          extra={
            <Button variant="primary" onClick={() => navigate("/forgot-password")}>
              {t("reset.requestNew")}
            </Button>
          }
        />
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <Result
          status="success"
          title={t("reset.successTitle")}
          subTitle={t("reset.successBody")}
          extra={
            <Button variant="primary" onClick={() => navigate("/login")}>
              {t("verify.goLogin")}
            </Button>
          }
        />
      </AuthShell>
    );
  }

  const pwdError = pwdRejected && !passwordMeetsPolicy(newPassword) ? t("login.pwdWeak") : undefined;

  return (
    <AuthShell>
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        <Title level={3}>{t("reset.title")}</Title>
        <Text secondary>{t("reset.subtitle")}</Text>
        {errs.formError && <ErrorAlert error={errs.formError} />}
        <form onSubmit={onSubmit}>
          <PasswordInput
            label={t("login.newPassword")}
            autoComplete="new-password"
            prefix={<Icon name="lock" size={14} />}
            value={newPassword}
            onChange={(ev) => setNewPassword(ev.target.value)}
            error={pwdError}
            autoFocus
            required
          />
          <PasswordChecklist value={newPassword} />
          <Button variant="primary" block loading={busy} type="submit">
            {t("reset.submit")}
          </Button>
        </form>
        <div className="auth-back">
          <Button variant="link" onClick={() => navigate("/login")}>
            {t("login.back")}
          </Button>
        </div>
      </Space>
    </AuthShell>
  );
}
